/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowDownUp, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Filter, Info, Plus, SlidersHorizontal,
} from "lucide-react";
import { downloadFile, can } from "@/utils/service";
import { notify } from "@/components/ui/dialogs";
import { apiMessage } from "@/utils/api";
import AppSelect from "@/components/ui/app-select";

/* ------------------------------------------------------------------------------------------------
 * Grid state shared by the toolbar, the statistics strip and the table: which columns are hidden
 * ("Customize") and whether statistics are shown. Both are remembered per page in this browser.
 * ---------------------------------------------------------------------------------------------- */

export interface GridColumn { id: string; label: string }

interface GridState {
  columns: GridColumn[];
  register: (cols: GridColumn[]) => void;
  hidden: Record<string, boolean>;
  toggle: (id: string) => void;
  reset: () => void;
  statsOn: boolean;
  setStatsOn: (on: boolean) => void;
}

const GridContext = createContext<GridState | null>(null);

const read = <V,>(key: string, fallback: V): V => {
  try { const v = localStorage.getItem(key); return v ? (JSON.parse(v) as V) : fallback; } catch { return fallback; }
};
const write = (key: string, value: unknown) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* per-viewer convenience only */ }
};

/** Wrap a table page (toolbar + stats + table) so they share column and statistics settings. */
export function GridProvider({ id, children }: { id: string; children: ReactNode }) {
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [hidden, setHidden] = useState<Record<string, boolean>>(() => read(`grid_hidden_${id}`, {}));
  const [statsOn, setStats] = useState<boolean>(() => read(`grid_stats_${id}`, true));
  const value = useMemo<GridState>(() => ({
    columns,
    register: (cols) => setColumns((prev) => (JSON.stringify(prev) === JSON.stringify(cols) ? prev : cols)),
    hidden,
    toggle: (col) => setHidden((h) => { const next = { ...h, [col]: !h[col] }; write(`grid_hidden_${id}`, next); return next; }),
    reset: () => { setHidden({}); write(`grid_hidden_${id}`, {}); },
    statsOn,
    setStatsOn: (on) => { setStats(on); write(`grid_stats_${id}`, on); },
  }), [columns, hidden, statsOn, id]);
  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
}

/** Used by TableProvider: registers the table's columns and returns which are hidden. */
export const useGridColumns = () => useContext(GridContext);

/* ------------------------------------------------------------------------------------------------
 * Small popover used by Filter, Sort and Customize. Closes on outside click and Escape.
 * ---------------------------------------------------------------------------------------------- */

function Popover({ button, children, align = "left", wide }: { button: (open: boolean, toggle: () => void) => ReactNode; children: (close: () => void) => ReactNode; align?: "left" | "right"; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const target = e.target as Element;
      // Pickers inside the popover (date calendars, selects) render in portals; clicks there stay "inside".
      if (target.closest?.("[data-radix-popper-content-wrapper], [data-radix-portal]")) return;
      if (ref.current && !ref.current.contains(target)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      {button(open, () => setOpen((o) => !o))}
      {open && (
        <div className={`absolute top-full z-40 mt-2 rounded-xl border border-gray-200 bg-white p-2 shadow-xl ${align === "right" ? "right-0" : "left-0"} ${wide ? "w-[min(22rem,calc(100vw-2rem))]" : "w-60"}`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

const toolButton = (active = false) =>
  `inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium shadow-sm transition ${active ? "border-primary/40 bg-primary/5 text-primary" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`;

/* ------------------------------------------------------------------------------------------------
 * Toolbar: [view switch] | Filter  Sort  Show statistics ......... Customize | Export  + Add
 * ---------------------------------------------------------------------------------------------- */

export interface SortOption { value: string; label: string }

export function GridToolbar({
  view, search, filters, activeFilters = 0, onClearFilters, sortOptions, sort, onSort, hasStats, exportEntity, exportPermission, onAdd, addLabel = "Add New",
}: {
  /** The page's view switch (Board / Table / Cards), shown first. */
  view?: ReactNode;
  /** A search input, shown next to the buttons. */
  search?: ReactNode;
  /** Filter controls shown in the Filter popover. */
  filters?: ReactNode;
  activeFilters?: number;
  onClearFilters?: () => void;
  sortOptions?: SortOption[];
  sort?: string;
  onSort?: (value: string) => void;
  /** The page shows a statistics strip, so offer the "Show statistics" switch. */
  hasStats?: boolean;
  /** `/exports/<entity>` CSV of everything the user can see. */
  exportEntity?: string;
  exportPermission?: string;
  onAdd?: () => void;
  addLabel?: string;
}) {
  const grid = useContext(GridContext);
  const [exporting, setExporting] = useState(false);
  const canExport = exportEntity && (!exportPermission || can(exportPermission));
  const sortLabel = sortOptions?.find((o) => o.value === sort)?.label;

  const doExport = async () => {
    if (!exportEntity) return;
    setExporting(true);
    try { await downloadFile(`/exports/${exportEntity}`, `${exportEntity}-${new Date().toISOString().slice(0, 10)}.csv`); }
    catch (e) { notify(apiMessage(e, "Could not export.")); }
    finally { setExporting(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {view}
      {view && <span className="hidden h-6 w-px bg-gray-200 sm:block" />}
      {search}
      {filters && (
        <Popover wide button={(open, toggle) => (
          <button type="button" onClick={toggle} aria-expanded={open} className={toolButton(activeFilters > 0)}>
            <Filter className="h-3.5 w-3.5" />Filter{activeFilters > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] text-white">{activeFilters}</span>}
          </button>
        )}>
          {(close) => (
            <div className="space-y-3 p-2">
              <div className="flex flex-col gap-2 [&_input]:w-full [&_[data-app-select]]:w-full">{filters}</div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                {onClearFilters ? <button type="button" onClick={() => { onClearFilters(); close(); }} className="text-xs text-gray-500 hover:text-gray-900">Clear filters</button> : <span />}
                <button type="button" onClick={close} className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white">Done</button>
              </div>
            </div>
          )}
        </Popover>
      )}
      {sortOptions && onSort && (
        <Popover button={(open, toggle) => (
          <button type="button" onClick={toggle} aria-expanded={open} className={toolButton()} title={sortLabel ? `Sorted: ${sortLabel}` : undefined}>
            <ArrowDownUp className="h-3.5 w-3.5" />Sort<span className="hidden text-gray-400 lg:inline">{sortLabel ? `· ${sortLabel}` : ""}</span>
          </button>
        )}>
          {(close) => sortOptions.map((o) => (
            <button key={o.value} type="button" onClick={() => { onSort(o.value); close(); }} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50">
              {o.label}{o.value === sort && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </Popover>
      )}
      {hasStats && grid && (
        <label className="ml-1 inline-flex cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-700">
          <span className="hidden sm:inline">Show Statistics</span><span className="sm:hidden">Stats</span>
          <button type="button" role="switch" aria-checked={grid.statsOn} onClick={() => grid.setStatsOn(!grid.statsOn)}
            className={`relative h-5 w-9 rounded-full transition ${grid.statsOn ? "bg-primary" : "bg-gray-300"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${grid.statsOn ? "left-[1.125rem]" : "left-0.5"}`} />
          </button>
        </label>
      )}

      <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:ml-auto sm:w-auto">
        {grid && grid.columns.length > 1 && (
          <Popover align="right" button={(open, toggle) => (
            <button type="button" onClick={toggle} aria-expanded={open} className={toolButton(Object.values(grid.hidden).some(Boolean))}>
              <SlidersHorizontal className="h-3.5 w-3.5" /><span className="hidden sm:inline">Customize</span>
            </button>
          )}>
            {() => (
              <div>
                <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Columns</p>
                {grid.columns.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
                    <input type="checkbox" className="accent-primary" checked={!grid.hidden[c.id]} onChange={() => grid.toggle(c.id)}
                      disabled={!grid.hidden[c.id] && grid.columns.filter((x) => !grid.hidden[x.id]).length === 1} />
                    {c.label}
                  </label>
                ))}
                <button type="button" onClick={grid.reset} className="mt-1 w-full rounded-lg px-3 py-2 text-left text-xs text-primary hover:bg-gray-50">Show all columns</button>
              </div>
            )}
          </Popover>
        )}
        {canExport && (
          <button type="button" onClick={doExport} disabled={exporting} className={toolButton()}>
            <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">{exporting ? "Exporting…" : "Export"}</span>
          </button>
        )}
        {onAdd && (
          <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-black">
            <Plus className="h-3.5 w-3.5" />{addLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Statistics strip. Every number comes from the API; `hint` is a short factual line under it.
 * ---------------------------------------------------------------------------------------------- */

export interface Stat { label: string; value: ReactNode; hint?: ReactNode; info?: string; tone?: "default" | "good" | "warn" | "bad" }

export function StatsStrip({ items, loading }: { items: Stat[]; loading?: boolean }) {
  const grid = useContext(GridContext);
  if (grid && !grid.statsOn) return null;
  if (!items.length) return null;
  const tone = { default: "text-gray-900", good: "text-green-600", warn: "text-amber-600", bad: "text-red-600" };
  return (
    <div className={`grid overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${items.length >= 4 ? "grid-cols-2 lg:grid-cols-4" : items.length === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
      {items.map((s, i) => (
        <div key={s.label} className={`p-4 sm:p-5 ${i > 0 ? "border-gray-100 sm:border-l" : ""} ${i >= 2 && items.length >= 4 ? "border-t lg:border-t-0" : ""} ${i % 2 === 1 && items.length >= 4 ? "border-l" : ""}`}>
          <p className="flex items-center gap-1 text-xs text-gray-500">{s.label}{s.info && <span title={s.info}><Info className="h-3 w-3 text-gray-400" /></span>}</p>
          {loading ? <div className="mt-2 h-7 w-20 animate-pulse rounded bg-gray-100" /> : <p className={`mt-1 truncate text-xl font-semibold sm:text-2xl ${tone[s.tone ?? "default"]}`}>{s.value}</p>}
          {s.hint && <p className="mt-1 truncate text-[11px] text-gray-500">{s.hint}</p>}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Footer: Showing per page [n]   « ‹ 1 2 3 … 25 › »   Go to page [ ] Go
 * ---------------------------------------------------------------------------------------------- */

const pageList = (page: number, total: number): Array<number | "…"> => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, 2, total, page - 1, page, page + 1].filter((n) => n >= 1 && n <= total));
  if (page <= 3) [3, 4].forEach((n) => set.add(n));
  if (page >= total - 2) [total - 3, total - 2, total - 1].forEach((n) => set.add(n));
  const sorted = [...set].sort((a, b) => a - b);
  const out: Array<number | "…"> = [];
  sorted.forEach((n, i) => { if (i > 0 && n - sorted[i - 1] > 1) out.push("…"); out.push(n); });
  return out;
};

export function PaginationBar({ page, totalPages, total, perPage, onPage, onPerPage, noun = "records", perPageOptions = [10, 15, 25, 50, 100] }: {
  page: number; totalPages: number; total: number; perPage: number; onPage: (page: number) => void; onPerPage?: (n: number) => void; noun?: string; perPageOptions?: number[];
}) {
  const [goto, setGoto] = useState("");
  const pages = Math.max(totalPages, 1);
  const go = (n: number) => { const p = Math.min(Math.max(1, Math.trunc(n)), pages); if (p !== page) onPage(p); };
  const submit = () => { const n = Number(goto); if (Number.isFinite(n) && n >= 1) { go(n); setGoto(""); } };
  const from = total ? (page - 1) * perPage + 1 : 0;
  const to = Math.min(page * perPage, total);
  const base = "flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs transition disabled:cursor-not-allowed disabled:opacity-40";
  const nav = `${base} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`;
  const current = `${base} border-primary bg-primary font-semibold text-white`;

  return (
    <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 text-xs text-gray-600 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center justify-between gap-3 md:justify-start">
        {onPerPage ? (
          <label className="flex items-center gap-2">
            <span className="hidden sm:inline">Showing per page</span><span className="sm:hidden">Per page</span>
            <span className="relative">
              <AppSelect value={perPage} onChange={(e) => onPerPage(Number(e.target.value))} className="appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-7 text-xs font-medium text-gray-900">
                {[...new Set([...perPageOptions, perPage])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}</option>)}
              </AppSelect>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-500" />
            </span>
          </label>
        ) : <span />}
        <span className="text-gray-500">{total ? `${from}–${to} of ${total.toLocaleString()} ${noun}` : `No ${noun}`}</span>
      </div>

      <nav aria-label="Pagination" className="flex items-center justify-center gap-1">
        <button type="button" aria-label="First page" className={`${nav} hidden sm:flex`} disabled={page <= 1} onClick={() => go(1)}><ChevronsLeft className="h-3.5 w-3.5" /></button>
        <button type="button" aria-label="Previous page" className={nav} disabled={page <= 1} onClick={() => go(page - 1)}><ChevronLeft className="h-3.5 w-3.5" /></button>
        {pageList(page, pages).map((n, i) => n === "…"
          ? <span key={`gap-${i}`} className="px-1 text-gray-400">…</span>
          : <button key={n} type="button" aria-current={n === page ? "page" : undefined} onClick={() => go(n)}
              className={`${n === page ? current : nav} ${Math.abs(n - page) > 1 && n !== 1 && n !== pages ? "hidden sm:flex" : ""}`}>{n}</button>)}
        <button type="button" aria-label="Next page" className={nav} disabled={page >= pages} onClick={() => go(page + 1)}><ChevronRight className="h-3.5 w-3.5" /></button>
        <button type="button" aria-label="Last page" className={`${nav} hidden sm:flex`} disabled={page >= pages} onClick={() => go(pages)}><ChevronsRight className="h-3.5 w-3.5" /></button>
      </nav>

      <form className="flex items-center justify-end gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label htmlFor="goto-page" className="text-gray-500">Go to page</label>
        <input id="goto-page" inputMode="numeric" value={goto} onChange={(e) => setGoto(e.target.value.replace(/\D/g, ""))} placeholder={String(page)}
          className="h-8 w-14 rounded-lg border border-gray-200 px-2 text-center text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
        <button type="submit" disabled={!goto} className="inline-flex items-center gap-0.5 text-xs font-semibold text-gray-900 disabled:text-gray-300">Go<ChevronRight className="h-3 w-3" /></button>
      </form>
    </div>
  );
}
